import { z } from 'zod'
import { asEnumType } from '@gqloom/zod'

// ============================================================================
// ENUMS
// ============================================================================

export const AutoLogoutMinutes = z
  .enum(['FIVE', 'TEN', 'FIFTEEN', 'THIRTY', 'SIXTY'])
  .register(asEnumType, {
    name: 'AutoLogoutMinutes',
    description: 'Auto logout duration options in minutes',
  })

export const BusinessProfileTab = z
  .enum(['HOME', 'SCHEDULE', 'CLIENTS', 'RETAIL', 'REPORTS'])
  .register(asEnumType, {
    name: 'BusinessProfileTab',
    description: 'Business profile tab options',
  })

export const ClientProfileTab = z
  .enum([
    'OVERVIEW',
    'INFORMATION',
    'PAYMENT',
    'PACKAGES',
    'SCHEDULE',
    'DOCUMENTS',
    'NOTES',
    'CONTACT_LOGS',
    'SOAP_SESSION',
  ])
  .register(asEnumType, {
    name: 'ClientProfileTab',
    description: 'Client profile tab options for system preferences',
  })

// ============================================================================
// COUNTS SCHEMA
// ============================================================================

export const locationOperationsCountsSchema = z.object({
  siteId: z.string().uuid(),
  customPropertiesCount: z.number().int().nonnegative(),
  policiesCount: z.number().int().nonnegative(),
  questionnaireFormsCount: z.number().int().nonnegative(),
  cancellationReasonsCount: z.number().int().nonnegative(),
  suspensionReasonsCount: z.number().int().nonnegative(),
  tabsDisplayedCount: z.number().int().nonnegative(),
  availableClientsTabsCount: z.number().int().nonnegative(),
})

export type LocationOperationsCountsSchema = z.infer<typeof locationOperationsCountsSchema>

// ============================================================================
// GENERIC REASON SCHEMAS (for cancellation and suspension reasons)
// ============================================================================

export const genericReasonSchema = z.object({
  id: z.string().optional(),
  reason: z.string().optional(),
  enabled: z.boolean().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

/**
 * Reason ready for selection in the membership cancel/suspend dialogs:
 * narrowed from `genericReasonSchema` so that `id` and `reason` are both
 * required (the dialog filters out disabled rows and rows missing those fields).
 */
export const displayableReasonSchema = z.object({
  id: z.string(),
  reason: z.string(),
})

export type DisplayableReason = z.infer<typeof displayableReasonSchema>

export const genericReasonCreateSchema = z.object({
  reason: z.string().optional(),
  enabled: z.boolean().optional(),
})

export const genericReasonUpdateSchema = z.object({
  id: z.string().optional(),
  reason: z.string().optional(),
  enabled: z.boolean().optional(),
})

// ============================================================================
// CANCELLATION REASONS SCHEMAS
// ============================================================================

export const cancellationReasonSchema = genericReasonSchema
export const cancellationReasonCreateSchema = genericReasonCreateSchema
export const cancellationReasonUpdateSchema = genericReasonUpdateSchema

export const cancellationReasonsBulkUpdateSchema = z.object({
  cancellationReasons: z.array(cancellationReasonSchema),
})

// ============================================================================
// SUSPENSION REASONS SCHEMAS
// ============================================================================

export const suspensionReasonSchema = genericReasonSchema
export const suspensionReasonCreateSchema = genericReasonCreateSchema
export const suspensionReasonUpdateSchema = genericReasonUpdateSchema

export const suspensionReasonsBulkUpdateSchema = z.object({
  suspensionReasons: z.array(suspensionReasonSchema),
})

export type TSuspensionReasonsUpdate = z.infer<typeof suspensionReasonsBulkUpdateSchema>

// ============================================================================
// SIGN UP FIELDS SCHEMAS
// ============================================================================

// Legacy schema - replaced by primaryProfileFieldSchema and emergencyContactFieldSchema
export const signUpCustomFieldsSchema = z.object({
  customPropertyId: z.string(),
  enabled: z.boolean().default(false),
  required: z.boolean().default(false),
})

// Primary Profile Information Fields
export const PrimaryProfileFields = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  EMAIL: 'email',
  PHONE: 'phone',
  GENDER: 'gender',
  BIRTHDAY: 'birthday',
  BARCODE: 'barcode',
  ADDRESS: 'address',
  ZIP_CODE: 'zipCode',
  HOW_DID_YOU_HEAR_ABOUT_US: 'howDidYouHeadAboutUs',
  OCCUPATION: 'occupation',
  PASSWORD: 'password',
  CONFIRM_PASSWORD: 'confirmPassword',
} as const

export type PrimaryProfileFields = (typeof PrimaryProfileFields)[keyof typeof PrimaryProfileFields]

// Emergency Contact Information Fields
export const EmergencyContactFields = {
  NAME: 'emergencyContactName',
  PHONE: 'emergencyContactPhone',
  EMAIL: 'emergencyContactEmail',
  ADDRESS: 'emergencyContactAddress',
  RELATION: 'emergencyContactRelation',
} as const

export type EmergencyContactFields =
  (typeof EmergencyContactFields)[keyof typeof EmergencyContactFields]

// Field configuration schema (reusable)
export const fieldConfigSchema = z.object({
  enabled: z.boolean().default(false),
  required: z.boolean().default(false),
})

// Explicit object schemas (GQLoom compatible)
export const primaryProfileConfigSchema = z
  .object({
    firstName: fieldConfigSchema.optional(),
    lastName: fieldConfigSchema.optional(),
    email: fieldConfigSchema.optional(),
    phone: fieldConfigSchema.optional(),
    gender: fieldConfigSchema.optional(),
    birthday: fieldConfigSchema.optional(),
    barcode: fieldConfigSchema.optional(),
    address: fieldConfigSchema.optional(),
    zipCode: fieldConfigSchema.optional(),
    howDidYouHeadAboutUs: fieldConfigSchema.optional(),
    occupation: fieldConfigSchema.optional(),
    password: fieldConfigSchema.optional(),
    confirmPassword: fieldConfigSchema.optional(),
  })
  .default({})

export const emergencyContactConfigSchema = z
  .object({
    emergencyContactName: fieldConfigSchema.optional(),
    emergencyContactPhone: fieldConfigSchema.optional(),
    emergencyContactEmail: fieldConfigSchema.optional(),
    emergencyContactAddress: fieldConfigSchema.optional(),
    emergencyContactRelation: fieldConfigSchema.optional(),
  })
  .default({})

// Base sign up fields schema (used for both business and client)
export const signUpFieldsBaseSchema = z.object({
  primaryProfile: primaryProfileConfigSchema.default({}),
  emergencyContact: emergencyContactConfigSchema.default({}),
  customProperties: z.array(signUpCustomFieldsSchema).default([]),
  formIds: z.array(z.string()).default([]),
})

// Business sign up fields (no policies needed)
export const businessSignUpFieldsSchema = signUpFieldsBaseSchema

// Client sign up fields (includes policies)
export const clientSignUpFieldsSchema = signUpFieldsBaseSchema.extend({
  policyIds: z.array(z.string()).default([]),
})

// ============================================================================
// SYSTEM PREFERENCES SCHEMAS
// ============================================================================

export const tabsDisplayedSchema = z.object({
  classes: z.boolean().optional(),
  packages: z.boolean().optional(),
  requestBooking: z.boolean().optional(),
  workshops: z.boolean().optional(),
})

export const availableClientsTabsSchema = z.object({
  information: z.boolean().optional(),
  assessment: z.boolean().optional(),
  notes: z.boolean().optional(),
  schedule: z.boolean().optional(),
  payment: z.boolean().optional(),
})

export const systemPreferencesSchema = z.object({
  tabsDisplayed: tabsDisplayedSchema.optional(),
  defaultBusinessProfileTab: BusinessProfileTab.optional(),
  availableClientsTabs: availableClientsTabsSchema.optional(),
  defaultClientTab: ClientProfileTab.optional(),
  showBusinessInfo: z.boolean().optional(),
})

// ============================================================================
// UPDATE SCHEMAS
// ============================================================================

// Custom fields update schema
export const signUpCustomFieldsUpdateSchema = z
  .object({
    customPropertyId: z.string(),
    enabled: z.boolean().optional(),
    required: z.boolean().optional(),
  })
  .optional()

// Update field configuration schema
export const fieldConfigUpdateSchema = z
  .object({
    enabled: z.boolean().optional(),
    required: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If required is true, enabled must also be true (or not explicitly set to false)
      if (data.required === true && data.enabled === false) {
        return false
      }
      return true
    },
    {
      message: 'Field cannot be required when it is disabled',
      path: ['required'], // This will attach the error to the required field
    }
  )

// Update schemas using explicit objects (GQLoom compatible)
export const primaryProfileConfigUpdateSchema = z.object({
  firstName: fieldConfigUpdateSchema,
  lastName: fieldConfigUpdateSchema,
  email: fieldConfigUpdateSchema,
  phone: fieldConfigUpdateSchema,
  gender: fieldConfigUpdateSchema,
  birthday: fieldConfigUpdateSchema,
  barcode: fieldConfigUpdateSchema,
  address: fieldConfigUpdateSchema,
  zipCode: fieldConfigUpdateSchema,
  howDidYouHeadAboutUs: fieldConfigUpdateSchema,
  occupation: fieldConfigUpdateSchema,
  password: fieldConfigUpdateSchema,
  confirmPassword: fieldConfigUpdateSchema,
})

export const emergencyContactConfigUpdateSchema = z.object({
  emergencyContactName: fieldConfigUpdateSchema,
  emergencyContactPhone: fieldConfigUpdateSchema,
  emergencyContactEmail: fieldConfigUpdateSchema,
  emergencyContactAddress: fieldConfigUpdateSchema,
  emergencyContactRelation: fieldConfigUpdateSchema,
})

// Base sign up fields update schema
export const signUpFieldsBaseUpdateSchema = z.object({
  primaryProfile: primaryProfileConfigUpdateSchema,
  emergencyContact: emergencyContactConfigUpdateSchema,
  customProperties: z.array(signUpCustomFieldsUpdateSchema).optional(),
  formIds: z.array(z.string()).optional(),
})

// Business sign up fields update (no policies)
export const businessSignUpFieldsUpdateSchema = signUpFieldsBaseUpdateSchema

// Client sign up fields update (includes policies)
export const clientSignUpFieldsUpdateSchema = signUpFieldsBaseUpdateSchema.extend({
  policyIds: z.array(z.string()).optional(),
})

// ============================================================================
// LOCATION OPERATIONS INPUT SCHEMAS
// ============================================================================

export const updateLocationOperationsMainInputSchema = z
  .object({
    autoLogout: z.boolean().optional(),
    autoLogoutMinutes: AutoLogoutMinutes.optional(),
    enableClientsToSignUp: z.boolean().optional(),
    /**
     * CORE-1425 (V1 parity): when true, reject any new client signup or
     * client-create whose phone matches an existing client anywhere in the
     * corporate. V1 column: `oc_trainer.prevent_duplicate_signup`.
     */
    preventDuplicatePhoneSignUp: z.boolean().optional(),
    /** CORE-1456: clients can book on behalf of their buddies. */
    enableBookABuddy: z.boolean().optional(),
    /** CORE-1456: members see all sessions their buddy is booked into. Gated on enableBookABuddy. */
    enableBuddySessionSharingVisibility: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If autoLogout is true, autoLogoutMinutes must be provided
      if (data.autoLogout === true && !data.autoLogoutMinutes) {
        return false
      }
      return true
    },
    {
      message: 'Auto logout minutes must be specified when auto logout is enabled',
      path: ['autoLogoutMinutes'],
    }
  )

export const updateLocationOperationsBusinessSignUpInputSchema = z.object({
  businessSignUpFields: businessSignUpFieldsUpdateSchema,
})

export const updateLocationOperationsClientSignUpInputSchema = z.object({
  clientSignUpFields: clientSignUpFieldsUpdateSchema,
})

export const updateLocationOperationsSuspensionReasonsInputSchema = z.object({
  suspensionReasons: z.array(suspensionReasonSchema),
})

export const updateSystemPreferencesInputSchema = z.object({
  systemPreferences: systemPreferencesSchema,
})

// ============================================================================
// TYPES
// ============================================================================

// Client Sign Up Fields types
export type SignUpCustomFields = z.infer<typeof signUpCustomFieldsSchema>
export type IBusinessSignUpFields = z.infer<typeof businessSignUpFieldsSchema>
export type IClientSignUpFields = z.infer<typeof clientSignUpFieldsSchema>

// Generic Reason types
export type GenericReason = z.infer<typeof genericReasonSchema>
export type GenericReasonCreate = z.infer<typeof genericReasonCreateSchema>
export type GenericReasonUpdate = z.infer<typeof genericReasonUpdateSchema>

// Cancellation Reasons types (backward compatibility)
export type CancellationReasonSchema = z.infer<typeof cancellationReasonSchema>
export type CancellationReasonCreateSchema = z.infer<typeof cancellationReasonCreateSchema>
export type CancellationReasonUpdateSchema = z.infer<typeof cancellationReasonUpdateSchema>
export type CancellationReasonsBulkUpdateSchema = z.infer<
  typeof cancellationReasonsBulkUpdateSchema
>
export type TCancellationReasonsUpdate = z.infer<typeof cancellationReasonsBulkUpdateSchema>

// Suspension Reasons types
export type SuspensionReasonSchema = z.infer<typeof suspensionReasonSchema>
export type SuspensionReasonCreateSchema = z.infer<typeof suspensionReasonCreateSchema>
export type SuspensionReasonUpdateSchema = z.infer<typeof suspensionReasonUpdateSchema>
export type SuspensionReasonsBulkUpdateSchema = z.infer<typeof suspensionReasonsBulkUpdateSchema>

// Client Sign Up Fields Update types
export type SignUpCustomFieldsUpdate = z.infer<typeof signUpCustomFieldsUpdateSchema>
export type BusinessSignUpFieldsUpdate = z.infer<typeof businessSignUpFieldsUpdateSchema>
export type ClientSignUpFieldsUpdate = z.infer<typeof clientSignUpFieldsUpdateSchema>

// Operations input types
export type UpdateLocationOperationsMainInput = z.infer<
  typeof updateLocationOperationsMainInputSchema
>
export type UpdateLocationOperationsBusinessSignUpInput = z.infer<
  typeof updateLocationOperationsBusinessSignUpInputSchema
>
export type UpdateLocationOperationsClientSignUpInput = z.infer<
  typeof updateLocationOperationsClientSignUpInputSchema
>
export type UpdateLocationOperationsSuspensionReasonsInput = z.infer<
  typeof updateLocationOperationsSuspensionReasonsInputSchema
>
export type UpdateSystemPreferencesInput = z.infer<typeof updateSystemPreferencesInputSchema>

// System Preferences types
export type SystemPreferences = z.infer<typeof systemPreferencesSchema>
export type TabsDisplayed = z.infer<typeof tabsDisplayedSchema>
export type AvailableClientsTabs = z.infer<typeof availableClientsTabsSchema>
